import { Icon, IconName } from "@/shared/components";
import { Separated } from "@/shared/components/ui/separated.tsx";
import { cn } from "@/shared/utils";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import * as React from "react";

function ToggleGroup({ className, children, ...props }: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
    return (
        <ToggleGroupPrimitive.Root
            data-slot="toggle-group"
            className={cn("group/toggle-group flex w-full min-w-0 flex-1 shrink-0 items-center", className)}
            {...props}
        >
            {children}
        </ToggleGroupPrimitive.Root>
    );
}

function ToggleGroupItem({ className, children, ...props }: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
    return (
        <ToggleGroupPrimitive.Item
            data-slot="toggle-group-item"
            className={cn(
                "text-s inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent p-1 px-2 py-1 whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            {children}
        </ToggleGroupPrimitive.Item>
    );
}

export type ToggleGroupOption<T extends string> = {
    label?: string;
    value: T;
    icon?: IconName;
};

type SingleToggleGroupProps<T extends string> = {
    options: ToggleGroupOption<T>[];
    value: T;
    onValueChange: (value: T) => void;
    className?: string;
    ["data-testid"]?: string;
};

function SingleToggleGroup<T extends string>({ options, value, onValueChange, className, ...props }: Readonly<SingleToggleGroupProps<T>>) {
    const dataTestId = props["data-testid"] ?? "single-toggle-group";
    return (
        <ToggleGroup
            type="single"
            value={value}
            onValueChange={(value: T) => {
                if (value) onValueChange(value);
            }}
            className={cn("bg-input border-input-default h-10 w-min gap-1 rounded-md border p-1", className)}
        >
            <Separated orientation="vertical">
                {options.map((option) => (
                    <ToggleGroupItem
                        key={option.value}
                        value={option.value}
                        id={option.value}
                        data-testid={`${dataTestId}-value`}
                        data-value={option.value}
                        data-state={value === option.value ? "active" : "inactive"}
                        className="group data-[state=active]:bg-selected-default data-[state=inactive]:hover:text-content-interactive-hover rounded-s px-4 py-2 transition-all duration-300"
                    >
                        {option.icon && <Icon className="flex items-center justify-center" name={option.icon} />}
                        {option.label && (
                            <span className="leading-l text-m group-data-[state=active]:text-content-selected group-data-[state=active]:font-bold">
                                {option.label}
                            </span>
                        )}
                    </ToggleGroupItem>
                ))}
            </Separated>
        </ToggleGroup>
    );
}

export { SingleToggleGroup, ToggleGroup, ToggleGroupItem };
